import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Truck, User, Package, CheckCircle, XCircle, Clock, Search, Filter, Calendar, Building2, FileText, Eye, Download, ClipboardCheck, Workflow, Zap, Gift, RotateCcw, AlertOctagon } from 'lucide-react';
import { FilterSection, useToast, LoadingState, EmptyState } from '../components/ui';
import Form103Modal from '../components/Form103Modal';
import api from '../services/api';

const GateEntryList = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    supplier: 'all',
    dateFrom: '',
    dateTo: ''
  });

  const [selectedTruck, setSelectedTruck] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDockModal, setShowDockModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showForm103Modal, setShowForm103Modal] = useState(false);
  const [selectedTruckForForm103, setSelectedTruckForForm103] = useState(null);
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [recommendedDock, setRecommendedDock] = useState(null);
  const [loadingRecommendation, setLoadingRecommendation] = useState(false);

  // Verification checklist state
  const [verificationChecks, setVerificationChecks] = useState({
    puc_certificate_valid: false,
    puc_certificate_number: '',
    puc_expiry_date: '',
    license_verified: false,
    alcohol_test_passed: false,
    vehicle_condition_good: false,
    safety_equipment_present: false,
    notes: ''
  });

  // Fetch truck arrivals with filters
  const { data: trucksData, isLoading } = useQuery({
    queryKey: ['gate-entries', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.supplier !== 'all') params.append('supplier', filters.supplier);
      if (filters.search) params.append('search', filters.search);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);

      const response = await api.get(`/gate/trucks/?${params.toString()}`);
      return response.data.results || response.data;
    }
  });

  // Fetch suppliers for filter
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const response = await api.get('/procurement/suppliers/?is_active=true');
      return response.data.results || response.data;
    }
  });

  // Status priority for sorting (lower number = higher priority)
  const STATUS_PRIORITY = {
    'ARRIVED': 1,
    'GATE_CHECK': 2,
    'DOCUMENTS_VERIFIED': 3,
    'DOCK_ASSIGNED': 4,
    'COMPLETED': 5,
    'SAFETY_CHECK_FAILED': 6,
    'REJECTED': 7,
  };

  // Sort trucks by status priority and dock assignment
  const sortedTrucksData = useMemo(() => {
    if (!trucksData || trucksData.length === 0) return [];

    return [...trucksData].sort((a, b) => {
      // First, sort by status priority
      const statusA = STATUS_PRIORITY[a.status] || 99;
      const statusB = STATUS_PRIORITY[b.status] || 99;

      if (statusA !== statusB) {
        return statusA - statusB;
      }

      // Within same status, sort by dock assignment (assigned docks first)
      const hasDockA = a.dock || a.dock_number ? 1 : 0;
      const hasDockB = b.dock || b.dock_number ? 1 : 0;

      if (hasDockA !== hasDockB) {
        return hasDockB - hasDockA; // Trucks with docks come first
      }

      // If both have docks, sort by dock number
      if (hasDockA && hasDockB) {
        const dockNumA = parseInt(a.dock_number || a.dock || '0');
        const dockNumB = parseInt(b.dock_number || b.dock || '0');
        return dockNumA - dockNumB;
      }

      // Finally, sort by arrival time (most recent first)
      return new Date(b.arrival_time || b.created_at) - new Date(a.arrival_time || a.created_at);
    });
  }, [trucksData]);

  // Get AI recommendation for dock
  const handleGetRecommendation = async (truckId) => {
    setLoadingRecommendation(true);
    try {
      const response = await api.post('/warehouse/docks/recommend_for_truck/', {
        truck_id: truckId,
        limit: 1
      });

      if (response.data.recommendations && response.data.recommendations.length > 0) {
        setRecommendedDock(response.data.recommendations[0]);
      } else {
        showError('No suitable dock found. Truck will be added to queue.');
      }
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to get recommendation');
    } finally {
      setLoadingRecommendation(false);
    }
  };

  // Confirm and assign the recommended dock
  const handleConfirmAssignment = async (truckId) => {
    setAutoAssigning(true);
    try {
      const response = await api.post('/warehouse/docks/auto_assign/', {
        truck_id: truckId
      });

      // Check if truck was queued instead of assigned
      if (response.data.queued) {
        success(response.data.message);
        // Show queue info
        console.log('Truck queued:', response.data.queue_info);
      } else {
        success(response.data.message || 'Dock auto-assigned successfully!');
      }

      queryClient.invalidateQueries(['gate-entries']);
      setShowDockModal(false);
      setSelectedTruck(null);
      setRecommendedDock(null);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to auto-assign dock');
    } finally {
      setAutoAssigning(false);
    }
  };

  // Form 103 success handler
  const handleForm103Success = (data) => {
    // Invalidate all truck queries to force refresh
    queryClient.invalidateQueries(['gate-entries']);
    queryClient.invalidateQueries(['truck-arrivals']);

    // Force immediate refetch
    queryClient.refetchQueries(['gate-entries']);

    setShowForm103Modal(false);
    setSelectedTruckForForm103(null);
    success(`Form 103 ${data.summary.grn_number} created successfully!`);
  };

  // Verify documents mutation
  const verifyDocumentsMutation = useMutation({
    mutationFn: async ({ truckId, verificationData }) => {
      const response = await api.post(`/gate/trucks/${truckId}/verify-documents/`, verificationData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['gate-entries']);
      setShowVerificationModal(false);
      setSelectedTruck(null);
      // Reset verification checks
      setVerificationChecks({
        puc_certificate_valid: false,
        puc_certificate_number: '',
        puc_expiry_date: '',
        license_verified: false,
        alcohol_test_passed: false,
        vehicle_condition_good: false,
        safety_equipment_present: false,
        notes: ''
      });
      success('Documents verified successfully!');
    },
    onError: (err) => {
      const errorMessage = err.response?.data?.puc_certificate_number?.[0] ||
                          err.response?.data?.error ||
                          err.response?.data?.detail ||
                          'Failed to verify documents';
      showError(errorMessage);
    }
  });

  const getStatusBadge = (status) => {
    const badges = {
      'ARRIVED': { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Arrived' },
      'GATE_CHECK': { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Gate Check' },
      'DOCUMENTS_VERIFIED': { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Docs Verified' },
      'DOCK_ASSIGNED': { color: 'bg-purple-100 text-purple-800', icon: Package, label: 'Dock Assigned' },
      'SAFETY_CHECK_FAILED': { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Safety Failed' },
      'REJECTED': { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' },
      'COMPLETED': { color: 'bg-gray-100 text-gray-800', icon: CheckCircle, label: 'Completed' },
    };

    const badge = badges[status] || badges['ARRIVED'];
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  // Workflow type badge
  const getWorkflowBadge = (workflow) => {
    if (!workflow) {
      return <span className="text-xs text-gray-400">No workflow</span>;
    }

    const workflowTypes = {
      'STANDARD': { color: 'bg-blue-100 text-blue-800', icon: Package, label: 'Standard' },
      'JIT': { color: 'bg-orange-100 text-orange-800', icon: Zap, label: 'JIT' },
      'FOC': { color: 'bg-green-100 text-green-800', icon: Gift, label: 'FOC' },
      'RETURN': { color: 'bg-yellow-100 text-yellow-800', icon: RotateCcw, label: 'Return' },
      'CRITICAL': { color: 'bg-red-100 text-red-800', icon: AlertOctagon, label: 'Critical' },
    };

    const workflowStates = {
      'ARRIVED': 'Arrived',
      'GATE_CHECK': 'Gate Check',
      'DOCUMENTS_VERIFIED': 'Docs Verified',
      'DOCK_ASSIGNED': 'Dock Assigned',
      'UNLOADING': 'Unloading',
      'QC_INSPECTION': 'QC Inspection',
      'GRN_CREATION': 'GRN Creation',
      'SAP_POSTED': 'SAP Posted',
      'COMPLETED': 'Completed',
    };

    const type = workflowTypes[workflow.workflow_type] || workflowTypes['STANDARD'];
    const Icon = type.icon;
    const state = workflowStates[workflow.current_state] || workflow.current_state;

    return (
      <div className="space-y-1">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${type.color}`}>
          <Icon className="w-3 h-3 mr-1" />
          {type.label}
        </span>
        <div className="flex items-center text-xs text-gray-600">
          <Workflow className="w-3 h-3 mr-1" />
          {state}
        </div>
      </div>
    );
  };

  const filterOptions = [
    {
      name: 'status',
      label: 'Status',
      value: filters.status,
      options: [
        { value: 'ARRIVED', label: 'Arrived' },
        { value: 'GATE_CHECK', label: 'Gate Check' },
        { value: 'DOCUMENTS_VERIFIED', label: 'Documents Verified' },
        { value: 'DOCK_ASSIGNED', label: 'Dock Assigned' },
        { value: 'SAFETY_CHECK_FAILED', label: 'Safety Check Failed' },
        { value: 'REJECTED', label: 'Rejected' },
        { value: 'COMPLETED', label: 'Completed' }
      ]
    },
    {
      name: 'supplier',
      label: 'Supplier',
      value: filters.supplier,
      options: suppliers.map(s => ({ value: s.id, label: s.name }))
    }
  ];

  const stats = [
    {
      label: 'Total Entries',
      value: sortedTrucksData?.length || 0,
      color: 'bg-blue-500',
      icon: Truck
    },
    {
      label: 'Awaiting Form 103',
      value: sortedTrucksData?.filter(t => t.status === 'DOCUMENTS_VERIFIED' && !t.has_form_103).length || 0,
      color: 'bg-amber-500',
      icon: ClipboardCheck
    },
    {
      label: 'Ready for Dock',
      value: sortedTrucksData?.filter(t => t.status === 'DOCUMENTS_VERIFIED' && t.has_form_103 && !t.dock).length || 0,
      color: 'bg-yellow-500',
      icon: Clock
    },
    {
      label: 'At Docks',
      value: sortedTrucksData?.filter(t => t.status === 'DOCK_ASSIGNED').length || 0,
      color: 'bg-purple-500',
      icon: Package
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Gate Entry List</h1>
        <p className="text-gray-600 mt-2">View and manage all truck arrivals</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm">{stat.label}</p>
                  <p className="text-3xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className={`${stat.color} text-white p-3 rounded-lg`}>
                  <Icon className="w-6 h-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="search"
                placeholder="Truck number, driver name..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="input-field pl-10"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="input-field"
            >
              <option value="all">All Status</option>
              {filterOptions[0].options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="input-field"
            />
          </div>

          {/* Date To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="input-field"
            />
          </div>
        </div>

        {/* Clear Filters */}
        {(filters.search || filters.status !== 'all' || filters.dateFrom || filters.dateTo) && (
          <div className="mt-4">
            <button
              onClick={() => setFilters({ search: '', status: 'all', supplier: 'all', dateFrom: '', dateTo: '' })}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Truck List Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <LoadingState message="Loading gate entries..." />
        ) : !sortedTrucksData || sortedTrucksData.length === 0 ? (
          <EmptyState
            icon={Truck}
            title="No gate entries found"
            message={filters.search || filters.status !== 'all' ? "Try adjusting your filters" : "No trucks registered yet"}
          />
        ) : (
          <div className="overflow-x-auto">
            <div className="px-4 py-2 bg-gray-100 text-xs text-gray-600 border-b">
              Sorted by: Status priority, then Dock assignment
            </div>
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Truck Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Driver
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Supplier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Arrival Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dock
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Workflow
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedTrucksData.map((truck) => (
                  <tr key={truck.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Truck className="w-5 h-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{truck.truck_number}</div>
                          {truck.gate_entry_number && (
                            <div className="text-xs text-gray-500">{truck.gate_entry_number}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <div className="text-sm text-gray-900">
                          {truck.driver?.name || truck.driver_name || 'Unknown'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                        <div className="text-sm text-gray-900">
                          {truck.supplier_name || 'Unknown'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(truck.arrival_time || truck.created_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {getStatusBadge(truck.status)}
                        {/* Show Form 103 status for DOCUMENTS_VERIFIED */}
                        {truck.status === 'DOCUMENTS_VERIFIED' && (
                          truck.has_form_103 ? (
                            <div className="flex items-center text-xs text-green-600 font-medium">
                              <ClipboardCheck className="w-3 h-3 mr-1" />
                              Form 103: {truck.form_103_grn_number}
                            </div>
                          ) : (
                            <div className="flex items-center text-xs text-amber-600 font-medium">
                              <Clock className="w-3 h-3 mr-1" />
                              Form 103 required
                            </div>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {truck.dock_number || truck.dock ? (
                        <div className="flex items-center text-sm text-green-600 font-medium">
                          <Package className="w-4 h-4 mr-1" />
                          {truck.dock_number || `Dock ${truck.dock}`}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getWorkflowBadge(truck.workflow)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={async () => {
                            // Fetch full details from API
                            try {
                              const response = await api.get(`/gate/trucks/${truck.id}/`);
                              setSelectedTruck(response.data);
                              setShowDetailsModal(true);
                            } catch (error) {
                              console.error('Error fetching truck details:', error);
                              showError('Failed to load truck details');
                            }
                          }}
                          className="text-blue-600 hover:text-blue-700"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>

                        {/* Show Verify Documents button for ARRIVED status */}
                        {truck.status === 'ARRIVED' && (
                          <button
                            onClick={() => {
                              setSelectedTruck(truck);
                              setShowVerificationModal(true);
                            }}
                            className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 flex items-center text-sm"
                            title="Verify documents and safety checks"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Verify Docs
                          </button>
                        )}

                        {/* Show Create Form 103 button for DOCUMENTS_VERIFIED status without Form 103 */}
                        {truck.status === 'DOCUMENTS_VERIFIED' && !truck.has_form_103 && (
                          <button
                            onClick={() => {
                              setSelectedTruckForForm103(truck);
                              setShowForm103Modal(true);
                            }}
                            className="bg-amber-500 text-white px-3 py-1 rounded hover:bg-amber-600 flex items-center text-sm"
                            title="Create Form 103 (Invoice Entry)"
                          >
                            <ClipboardCheck className="w-4 h-4 mr-1" />
                            Create Form 103
                          </button>
                        )}

                        {/* Show Assign Dock button for DOCUMENTS_VERIFIED status with Form 103 */}
                        {truck.status === 'DOCUMENTS_VERIFIED' && truck.has_form_103 && !truck.dock && (
                          <button
                            onClick={async () => {
                              // Fetch full details from API before showing dock modal
                              try {
                                const response = await api.get(`/gate/trucks/${truck.id}/`);
                                console.log('Full truck data from API:', response.data);
                                console.log('Supplier details:', response.data.supplier_details);
                                console.log('Dock details:', response.data.dock_details);
                                setSelectedTruck(response.data);
                                setShowDockModal(true);
                              } catch (error) {
                                console.error('Error fetching truck details:', error);
                                showError('Failed to load truck details');
                              }
                            }}
                            className="bg-purple-500 text-white px-3 py-1 rounded hover:bg-purple-600 flex items-center text-sm"
                            title="Assign dock to this truck"
                          >
                            <Package className="w-4 h-4 mr-1" />
                            Assign Dock
                          </button>
                        )}
                        {/* Show status if already assigned */}
                        {truck.dock && (
                          <span className="text-green-600 text-sm font-medium flex items-center">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            At Dock
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedTruck && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <h2 className="text-2xl font-bold mb-4">Truck Entry Details</h2>

            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                <div>
                  <p className="text-sm text-gray-600">Truck Number</p>
                  <p className="text-lg font-semibold">{selectedTruck.truck_number}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Gate Entry Number</p>
                  <p className="text-lg font-semibold">{selectedTruck.gate_entry_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedTruck.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Arrival Time</p>
                  <p className="text-md">{new Date(selectedTruck.arrival_time).toLocaleString('en-IN')}</p>
                </div>
              </div>

              {/* Driver Info */}
              <div className="pb-4 border-b">
                <h3 className="font-semibold mb-2">Driver Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="text-md">{selectedTruck.driver?.name || selectedTruck.driver_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">License Number</p>
                    <p className="text-md">{selectedTruck.driver?.license_number || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Supplier Info */}
              <div className="pb-4 border-b">
                <h3 className="font-semibold mb-2">Supplier Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="text-md">{selectedTruck.supplier_details?.name || selectedTruck.supplier_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Code</p>
                    <p className="text-md">{selectedTruck.supplier_details?.code || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="pb-4 border-b">
                <h3 className="font-semibold mb-2">Documents</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">LR Number</p>
                    <p className="text-md">{selectedTruck.lr_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Invoice Number</p>
                    <p className="text-md">{selectedTruck.invoice_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">E-way Bill</p>
                    <p className="text-md">{selectedTruck.eway_bill_number || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Dock Info */}
              {(selectedTruck.dock || selectedTruck.dock_details) && (
                <div>
                  <h3 className="font-semibold mb-2">Dock Assignment</h3>
                  <div className="bg-green-50 p-3 rounded">
                    <p className="text-green-800 font-medium">
                      {selectedTruck.dock_details?.dock_number ||
                       selectedTruck.dock?.dock_number ||
                       `Dock ${selectedTruck.dock_details?.id || selectedTruck.dock}`}
                    </p>
                    {selectedTruck.dock_details?.location && (
                      <p className="text-sm text-gray-600">
                        Location: {selectedTruck.dock_details.location}
                      </p>
                    )}
                    {selectedTruck.dock_assigned_at && (
                      <p className="text-sm text-green-600 mt-1">
                        Assigned: {new Date(selectedTruck.dock_assigned_at).toLocaleString('en-IN')}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Safety Verification History */}
              {selectedTruck.safety_logs && selectedTruck.safety_logs.length > 0 && (
                <div className="pb-4 border-b">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                    Safety Verification History
                  </h3>
                  <div className="space-y-2">
                    {selectedTruck.safety_logs.map((log) => (
                      <div
                        key={log.id}
                        className={`p-3 rounded-lg border ${
                          log.passed
                            ? 'bg-green-50 border-green-200'
                            : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            {log.passed ? (
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-600" />
                            )}
                            <span className="font-medium text-gray-900">
                              {log.check_type === 'PUC' && 'PUC Certificate'}
                              {log.check_type === 'LICENSE' && 'Driver License'}
                              {log.check_type === 'ALCOHOL' && 'Alcohol Test'}
                              {log.check_type === 'VEHICLE' && 'Vehicle Condition'}
                              {log.check_type === 'SAFETY_EQUIPMENT' && 'Safety Equipment'}
                            </span>
                            <span
                              className={`text-xs px-2 py-1 rounded ${
                                log.passed
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {log.passed ? 'PASSED' : 'FAILED'}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-600 flex items-center space-x-4">
                          <span className="flex items-center">
                            <User className="w-3 h-3 mr-1" />
                            Verified by: <strong className="ml-1">{log.checked_by_name || 'Unknown'}</strong>
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {new Date(log.checked_at).toLocaleString('en-IN')}
                          </span>
                        </div>
                        {log.remarks && (
                          <p className="mt-2 text-xs text-gray-700 italic">
                            Note: {log.remarks}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                  {selectedTruck.gate_officer && selectedTruck.gate_entry_time && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-900">
                        <strong>Gate Officer:</strong> {selectedTruck.gate_officer || 'N/A'}
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        Entry approved at: {new Date(selectedTruck.gate_entry_time).toLocaleString('en-IN')}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              {selectedTruck.notes && (
                <div>
                  <h3 className="font-semibold mb-2">Notes</h3>
                  <p className="text-sm text-gray-600">{selectedTruck.notes}</p>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                setShowDetailsModal(false);
                setSelectedTruck(null);
              }}
              className="w-full mt-6 bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Dock Assignment Modal */}
      {showDockModal && selectedTruck && (
        <div key={selectedTruck.id} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full animate-scale-in">
            <h2 className="text-2xl font-bold mb-4">🤖 Assign Dock</h2>

            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Truck Number</p>
              <p className="text-lg font-bold">{selectedTruck.truck_number}</p>

              <p className="text-sm text-gray-600 mt-2">Driver</p>
              <p className="text-md">
                {selectedTruck.driver?.name || selectedTruck.driver_name || 'Unknown'}
              </p>

              <p className="text-sm text-gray-600 mt-2">Supplier</p>
              <p className="text-md">
                {selectedTruck.supplier_details && selectedTruck.supplier_details.name
                  ? selectedTruck.supplier_details.name
                  : (selectedTruck.supplier_name || 'Unknown')}
              </p>

              {selectedTruck.supplier_details && selectedTruck.supplier_details.code && (
                <>
                  <p className="text-sm text-gray-600 mt-2">Supplier Code</p>
                  <p className="text-md">{selectedTruck.supplier_details.code}</p>
                </>
              )}
            </div>

            <div className="space-y-3">
              {!recommendedDock ? (
                <button
                  onClick={() => handleGetRecommendation(selectedTruck.id)}
                  disabled={loadingRecommendation}
                  className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-3 rounded-lg hover:from-purple-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loadingRecommendation ? (
                    <>
                      <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Getting Recommendation...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      🤖 Get AI Recommendation
                    </>
                  )}
                </button>
              ) : (
                <>
                  {/* Show recommended dock details */}
                  <div className="bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-400 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-green-800 flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Recommended Dock
                      </h3>
                      <span className="text-sm bg-green-200 text-green-800 px-2 py-1 rounded font-semibold">
                        Score: {recommendedDock.score}/100
                      </span>
                    </div>

                    <div className="space-y-2 mb-3">
                      <div className="flex justify-between">
                        <span className="text-sm font-semibold text-gray-700">Dock Number:</span>
                        <span className="text-sm font-bold text-gray-900">{recommendedDock.dock_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-semibold text-gray-700">Location:</span>
                        <span className="text-sm text-gray-900">{recommendedDock.location}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-semibold text-gray-700">Type:</span>
                        <span className="text-sm text-gray-900">{recommendedDock.dock_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-semibold text-gray-700">Material Type:</span>
                        <span className="text-sm text-gray-900">{recommendedDock.material_type}</span>
                      </div>
                    </div>

                    <div className="bg-white p-2 rounded text-xs text-gray-700">
                      <strong>Why this dock?</strong> {recommendedDock.recommendation}
                    </div>
                  </div>

                  {/* Confirm Assignment Button */}
                  <button
                    onClick={() => handleConfirmAssignment(selectedTruck.id)}
                    disabled={autoAssigning}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3 rounded-lg hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-semibold"
                  >
                    {autoAssigning ? (
                      <>
                        <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Assigning...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        ✅ Confirm & Assign This Dock
                      </>
                    )}
                  </button>

                  {/* Back button to get different recommendation */}
                  <button
                    onClick={() => setRecommendedDock(null)}
                    disabled={autoAssigning}
                    className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                  >
                    ← Get Different Recommendation
                  </button>
                </>
              )}

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              <button
                onClick={() => {
                  window.location.href = '/dock-management';
                }}
                className="w-full bg-blue-500 text-white px-4 py-3 rounded-lg hover:bg-blue-600 flex items-center justify-center"
              >
                <Package className="w-5 h-5 mr-2" />
                Manual Dock Selection
              </button>

              <button
                onClick={() => {
                  setShowDockModal(false);
                  setSelectedTruck(null);
                  setRecommendedDock(null);
                }}
                className="w-full bg-gray-200 text-gray-800 px-4 py-3 rounded-lg hover:bg-gray-300"
                disabled={autoAssigning || loadingRecommendation}
              >
                Cancel
              </button>
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-xs text-blue-800">
                💡 <strong>AI Auto-Assign</strong> uses intelligent scoring based on material type, performance, capacity, and facilities.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form 103 Modal */}
      {showForm103Modal && selectedTruckForForm103 && (
        <Form103Modal
          truck={selectedTruckForForm103}
          isOpen={showForm103Modal}
          onClose={() => {
            setShowForm103Modal(false);
            setSelectedTruckForForm103(null);
          }}
          onSuccess={handleForm103Success}
        />
      )}

      {/* Document Verification Modal */}
      {showVerificationModal && selectedTruck && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-500 to-green-600 text-white">
              <h2 className="text-2xl font-bold flex items-center">
                <CheckCircle className="w-6 h-6 mr-3" />
                Document & Safety Verification
              </h2>
              <p className="text-sm mt-2 text-green-100">
                Truck: <span className="font-semibold">{selectedTruck.truck_number}</span> |
                Supplier: <span className="font-semibold">{selectedTruck.supplier_details?.name}</span>
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* PUC Certificate Section */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-blue-600" />
                  PUC Certificate Verification
                </h3>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={verificationChecks.puc_certificate_valid}
                      onChange={(e) => setVerificationChecks({
                        ...verificationChecks,
                        puc_certificate_valid: e.target.checked
                      })}
                      className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                    />
                    <span className="text-gray-900 font-medium">PUC Certificate is Valid</span>
                  </label>

                  {verificationChecks.puc_certificate_valid && (
                    <div className="ml-8 space-y-2">
                      <input
                        type="text"
                        placeholder="PUC Certificate Number *"
                        value={verificationChecks.puc_certificate_number}
                        onChange={(e) => setVerificationChecks({
                          ...verificationChecks,
                          puc_certificate_number: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <input
                        type="date"
                        placeholder="PUC Expiry Date"
                        value={verificationChecks.puc_expiry_date}
                        onChange={(e) => setVerificationChecks({
                          ...verificationChecks,
                          puc_expiry_date: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Safety Checks Section */}
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-amber-600" />
                  Safety Checks
                </h3>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={verificationChecks.license_verified}
                      onChange={(e) => setVerificationChecks({
                        ...verificationChecks,
                        license_verified: e.target.checked
                      })}
                      className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                    />
                    <span className="text-gray-900">Driver License Verified</span>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={verificationChecks.alcohol_test_passed}
                      onChange={(e) => setVerificationChecks({
                        ...verificationChecks,
                        alcohol_test_passed: e.target.checked
                      })}
                      className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                    />
                    <span className="text-gray-900">Alcohol Test Passed</span>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={verificationChecks.vehicle_condition_good}
                      onChange={(e) => setVerificationChecks({
                        ...verificationChecks,
                        vehicle_condition_good: e.target.checked
                      })}
                      className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                    />
                    <span className="text-gray-900">Vehicle Condition is Good</span>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={verificationChecks.safety_equipment_present}
                      onChange={(e) => setVerificationChecks({
                        ...verificationChecks,
                        safety_equipment_present: e.target.checked
                      })}
                      className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                    />
                    <span className="text-gray-900">Safety Equipment Present</span>
                  </label>
                </div>
              </div>

              {/* Notes Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Notes
                </label>
                <textarea
                  value={verificationChecks.notes}
                  onChange={(e) => setVerificationChecks({
                    ...verificationChecks,
                    notes: e.target.value
                  })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                  placeholder="Add any additional notes about the verification..."
                />
              </div>

              {/* Validation Warning */}
              {verificationChecks.puc_certificate_valid && !verificationChecks.puc_certificate_number && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-800">
                    ⚠️ PUC Certificate Number is required when certificate is marked as valid
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  {Object.values({
                    puc_certificate_valid: verificationChecks.puc_certificate_valid,
                    license_verified: verificationChecks.license_verified,
                    alcohol_test_passed: verificationChecks.alcohol_test_passed,
                    vehicle_condition_good: verificationChecks.vehicle_condition_good,
                    safety_equipment_present: verificationChecks.safety_equipment_present
                  }).filter(Boolean).length} / 5 checks completed
                </div>
                <div className="space-x-3">
                  <button
                    onClick={() => {
                      setShowVerificationModal(false);
                      setSelectedTruck(null);
                      setVerificationChecks({
                        puc_certificate_valid: false,
                        puc_certificate_number: '',
                        puc_expiry_date: '',
                        license_verified: false,
                        alcohol_test_passed: false,
                        vehicle_condition_good: false,
                        safety_equipment_present: false,
                        notes: ''
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const allChecksPassed =
                        verificationChecks.puc_certificate_valid &&
                        verificationChecks.license_verified &&
                        verificationChecks.alcohol_test_passed &&
                        verificationChecks.vehicle_condition_good &&
                        verificationChecks.safety_equipment_present;

                      if (!allChecksPassed) {
                        showError('All safety checks must be completed before verification');
                        return;
                      }

                      if (verificationChecks.puc_certificate_valid && !verificationChecks.puc_certificate_number) {
                        showError('PUC Certificate Number is required');
                        return;
                      }

                      verifyDocumentsMutation.mutate({
                        truckId: selectedTruck.id,
                        verificationData: verificationChecks
                      });
                    }}
                    disabled={verifyDocumentsMutation.isLoading}
                    className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {verifyDocumentsMutation.isLoading ? 'Verifying...' : 'Verify & Approve'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GateEntryList;
